import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { History, Brain, GitBranch, Shield, Code, Bug, Settings } from 'lucide-react';
import PatchHistoryPage from '../patch-history.js';
import AgentMemoryPage from '../agent-memory.js';
import ProvenancePage from '../provenance.js';

type DebugFeature = 'patchHistory' | 'agentMemory' | 'provenance' | 'security';

const SecuritySettingsPanel: React.FC = () => {
  const [scanUrl, setScanUrl] = useState('http://localhost:3310/scan');
  const [maxFileSize, setMaxFileSize] = useState(50);
  const [autoScanEnabled, setAutoScanEnabled] = useState(true);
  const [quarantineEnabled, setQuarantineEnabled] = useState(true);

  return (
    <div className="h-full bg-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-500/20 rounded-lg">
            <Shield className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white text-drop-shadow">Security Settings</h1>
            <p className="text-slate-400">Configure malware scanning and security policies</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50">
            <h3 className="text-lg font-semibold text-white mb-4">Malware Scanning</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  ClamAV Service URL
                </label>
                <input
                  type="text"
                  value={scanUrl}
                  onChange={(e) => setScanUrl(e.target.value)}
                  className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  placeholder="http://localhost:3310/scan"
                />
                <p className="text-xs text-slate-400 mt-1">URL of the ClamAV scanning microservice</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Maximum File Size (MB)
                </label>
                <input
                  type="number"
                  value={maxFileSize}
                  onChange={(e) => setMaxFileSize(Number(e.target.value))}
                  className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  min="1"
                  max="500"
                />
                <p className="text-xs text-slate-400 mt-1">Files larger than this will be rejected before scanning</p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-white">Auto-scan uploads</h4>
                  <p className="text-xs text-slate-400">Automatically scan all uploaded files</p>
                </div>
                <button
                  onClick={() => setAutoScanEnabled(!autoScanEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    autoScanEnabled ? 'bg-green-600' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoScanEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-white">Quarantine infected files</h4>
                  <p className="text-xs text-slate-400">Move infected files to quarantine instead of deletion</p>
                </div>
                <button
                  onClick={() => setQuarantineEnabled(!quarantineEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    quarantineEnabled ? 'bg-green-600' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      quarantineEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50">
            <h3 className="text-lg font-semibold text-white mb-4">Security Status</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium text-white">Files Scanned</span>
                </div>
                <div className="text-2xl font-bold text-green-400">1,247</div>
                <div className="text-xs text-slate-400">Last 30 days</div>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bug className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-medium text-white">Threats Detected</span>
                </div>
                <div className="text-2xl font-bold text-red-400">3</div>
                <div className="text-xs text-slate-400">Last 30 days</div>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Settings className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-white">Service Status</span>
                </div>
                <div className="text-2xl font-bold text-green-400">Online</div>
                <div className="text-xs text-slate-400">ClamAV service</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BeehiveAdminDashboard: React.FC = () => {
  const [activeDebugFeature, setActiveDebugFeature] = useState<DebugFeature>('patchHistory');

  const debugFeatures = [
    {
      id: 'patchHistory' as DebugFeature,
      name: 'Patch History',
      icon: History,
      description: 'Track AI code changes and patches',
      component: PatchHistoryPage
    },
    {
      id: 'agentMemory' as DebugFeature,
      name: 'Agent Memory',
      icon: Brain,
      description: 'Inspect AI agent memory and context',
      component: AgentMemoryPage
    },
    {
      id: 'provenance' as DebugFeature,
      name: 'Provenance',
      icon: GitBranch,
      description: 'Audit trail of AI reasoning and decisions',
      component: ProvenancePage
    },
    {
      id: 'security' as DebugFeature,
      name: 'Security Settings',
      icon: Shield,
      description: 'Configure malware scanning and security policies',
      component: () => <SecuritySettingsPanel />
    }
  ];

  const renderActiveComponent = () => {
    switch (activeDebugFeature) {
      case 'patchHistory':
        return <PatchHistoryPage isOpen={true} onClose={() => console.log('Patch history closed')} fullScreen={true} />;
      case 'agentMemory':
        return <AgentMemoryPage isOpen={true} onClose={() => console.log('Agent memory closed')} fullScreen={true} />;
      case 'provenance':
        return <ProvenancePage isOpen={true} onClose={() => console.log('Provenance closed')} fullScreen={true} />;
      case 'security':
        return <SecuritySettingsPanel />;
      default:
        return <PatchHistoryPage isOpen={true} onClose={() => console.log('Patch history closed')} fullScreen={true} />;
    }
  };

  return (
    <div className="flex h-full w-full bg-slate-900" data-devinid="workspace-dashboard-panel">
      <div className="w-80 glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-r border-slate-700/30 flex flex-col">
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white text-drop-shadow" data-devinid="dashboard-header">Debug Dashboard</h1>
              <p className="text-sm text-slate-400">Internal Development Tools</p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Bug className="w-4 h-4 text-red-400" />
              <span className="text-sm font-medium text-red-400">Internal Use Only</span>
            </div>
            <p className="text-xs text-red-300/80">
              These tools expose AI reasoning processes and are for debugging purposes only.
            </p>
          </div>
        </div>

        <div className="flex-1 p-4">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
            Debug Tools
          </h3>
          <div className="space-y-2" data-devinid="dashboard-navigation-grid">
            {debugFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <button
                  key={feature.id}
                  onClick={() => setActiveDebugFeature(feature.id)}
                  className={`w-full flex items-start gap-3 p-3 text-left rounded-lg transition-all duration-200 ${
                    activeDebugFeature === feature.id
                      ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 text-white border border-red-500/30'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">{feature.name}</div>
                    <div className="text-xs text-slate-400 mt-1">{feature.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 border-t border-slate-700/50">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Code className="w-3 h-3" />
            <span>Development Environment Only</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <motion.div
          key={activeDebugFeature}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-1"
        >
          {renderActiveComponent()}
        </motion.div>
      </div>
    </div>
  );
};

export default BeehiveAdminDashboard;
